/**
 * PsicoSalud — Sala de videollamada embebida (Módulo 3).
 *
 * Carga la External API de Jitsi Meet desde el dominio configurado y
 * monta la sala dentro de la aplicación. El sistema orquesta la sesión
 * (crea la sala, emite el token, la vincula a la cita y al historial)
 * sin implementar un motor propio de videollamadas, tal como plantea la
 * ficha del proyecto.
 *
 * El script se carga una sola vez por dominio y se reutiliza: cada
 * inserción adicional volvería a descargar ~200 KB.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { JoinSession } from '@/types';

declare global {
  interface Window {
    JitsiMeetExternalAPI?: new (domain: string, options: Record<string, unknown>) => JitsiApi;
  }
}

interface JitsiApi {
  dispose: () => void;
  addEventListener: (event: string, handler: (payload?: unknown) => void) => void;
}

const loadedScripts = new Map<string, Promise<void>>();

function loadExternalApi(domain: string): Promise<void> {
  const existing = loadedScripts.get(domain);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://${domain}/external_api.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`No se pudo cargar external_api.js desde ${domain}`));
    document.head.appendChild(script);
  });

  loadedScripts.set(domain, promise);
  return promise;
}

interface Props {
  session: JoinSession;
  displayName: string;
  onLeave?: () => void;
}

export default function JitsiRoom({ session, displayName, onLeave }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<JitsiApi | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let disposed = false;
    const { domain, roomName, jwt, configOverwrite, interfaceConfigOverwrite } = session.embed_config;

    async function mount() {
      try {
        await loadExternalApi(domain);
        if (disposed || !containerRef.current || !window.JitsiMeetExternalAPI) return;

        // El token solo viaja si el backend lo aplicó. Mandarlo a un servidor
        // que no lo valida —como `meet.jit.si`— hace que Jitsi muestre un
        // error de autenticación y no deje entrar, así que se omite entero
        // en lugar de mandarlo vacío.
        const opciones: Record<string, unknown> = {
          roomName,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          configOverwrite,
          interfaceConfigOverwrite,
          userInfo: { displayName },
        };
        if (jwt) opciones.jwt = jwt;

        const api = new window.JitsiMeetExternalAPI(domain, opciones);

        api.addEventListener('videoConferenceLeft', () => onLeave?.());
        apiRef.current = api;
        setStatus('ready');
      } catch {
        if (!disposed) setStatus('error');
      }
    }

    void mount();

    return () => {
      disposed = true;
      apiRef.current?.dispose();
      apiRef.current = null;
    };
  }, [session, displayName, onLeave]);

  return (
    <>
      {status === 'loading' && <p className="t-suave">{t('video.connecting')}</p>}
      {status === 'error' && (
        <div className="notice notice-error">
          {t('errors.network')}{' '}
          <a href={session.meeting_url} target="_blank" rel="noreferrer">
            {t('video.join')}
          </a>
        </div>
      )}
      <div ref={containerRef} className="jitsi-container" data-cy="jitsi-container" />
    </>
  );
}
